/**
 * PieChartCard Component
 * Reusable donut chart with center label and legend
 *
 * @description Shared pie/donut chart for analytics dashboards (admin and provider)
 */

import { TrendingUp } from "lucide-react";
import { useCallback } from "react";
import { Label, Pie, PieChart } from "recharts";
import type { Props as LabelProps } from "recharts/types/component/Label";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "../ui/card";
import type { ChartConfig } from "../ui/chart";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "../ui/chart";
import { cn } from "../ui/cn";

interface PieChartDataItem {
  readonly category: string;
  readonly value: number;
  readonly fill: string;
  readonly [key: string]: string | number; // Index signature for recharts 3.x
}

interface PieChartCardProps {
  readonly data: PieChartDataItem[];
  readonly title: string;
  readonly description?: string;
  readonly chartConfig: ChartConfig;
  readonly totalLabel?: string;
  readonly footerText?: string;
  /** Optional className for card container (e.g., h-full for grid layouts) */
  readonly className?: string;
}

/**
 * Center label component for pie chart
 * Uses foreignObject to embed HTML for proper CSS flexbox centering
 */
function CenterLabel({
  viewBox,
  total,
  totalLabel,
}: {
  readonly viewBox: { cx?: number; cy?: number };
  readonly total: number;
  readonly totalLabel: string;
}) {
  if (!viewBox.cx || !viewBox.cy) return null;

  // Use native SVG text elements for reliable centering
  // Legend is now outside PieChart, so viewBox.cy is the true center
  return (
    <g>
      <text
        x={viewBox.cx}
        y={viewBox.cy - 8}
        textAnchor="middle"
        dominantBaseline="central"
        className="fill-white text-2xl font-bold"
      >
        {total.toLocaleString()}
      </text>
      <text
        x={viewBox.cx}
        y={viewBox.cy + 14}
        textAnchor="middle"
        dominantBaseline="central"
        className="fill-neutral-400 text-xs"
      >
        {totalLabel}
      </text>
    </g>
  );
}

export function PieChartCard({
  data,
  title,
  description,
  chartConfig,
  totalLabel = "Total",
  footerText,
  className,
}: PieChartCardProps) {
  const total = data.reduce((sum, item) => sum + item.value, 0);

  // Memoized render callback for Label content (extracted to avoid inline function definition)
  const renderCenterLabel = useCallback(
    (props: LabelProps) => {
      const { viewBox } = props;
      if (viewBox && "cx" in viewBox && "cy" in viewBox) {
        return <CenterLabel viewBox={viewBox} total={total} totalLabel={totalLabel} />;
      }
      return null;
    },
    [total, totalLabel],
  );

  if (total === 0) {
    return (
      <Card className={cn("border-neutral-800 bg-neutral-900/50 flex flex-col", className)}>
        <CardHeader>
          <CardTitle className="text-white">{title}</CardTitle>
          {description && <CardDescription className="text-neutral-400">{description}</CardDescription>}
        </CardHeader>
        <CardContent className="flex-1">
          <div className="flex items-center justify-center h-[300px] text-neutral-500">No data available</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      className={cn(
        "border-neutral-800 bg-neutral-900/50 hover:bg-neutral-900/70 transition-colors flex flex-col",
        className,
      )}
    >
      <CardHeader>
        <CardTitle className="text-white">{title}</CardTitle>
        {description && <CardDescription className="text-neutral-400">{description}</CardDescription>}
      </CardHeader>
      <CardContent className="flex-1 pb-0">
        <ChartContainer config={chartConfig} className="mx-auto aspect-square max-h-[250px]">
          <PieChart>
            <ChartTooltip cursor={false} content={<ChartTooltipContent hideLabel />} />
            <Pie data={data} dataKey="value" nameKey="category" innerRadius={60} strokeWidth={5}>
              <Label content={renderCenterLabel} />
            </Pie>
          </PieChart>
        </ChartContainer>
        {/* Legend outside PieChart to prevent it from affecting pie centering */}
        <div className="flex flex-wrap justify-center gap-2 mt-2">
          {data.map((item) => (
            <div key={item.category} className="flex items-center gap-1.5 text-xs">
              <div className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: item.fill }} />
              <span className="text-neutral-400 capitalize">{chartConfig[item.category]?.label ?? item.category}</span>
            </div>
          ))}
        </div>
      </CardContent>
      {footerText && (
        <CardFooter className="flex-col gap-2 text-sm">
          <div className="flex items-center gap-2 font-medium leading-none text-white">
            {total.toLocaleString()} total <TrendingUp className="h-4 w-4 text-lumi-accent" />
          </div>
          <div className="leading-none text-neutral-500">{footerText}</div>
        </CardFooter>
      )}
    </Card>
  );
}
